/**
 * useReleaseAgentAutoOpen Hook
 *
 * @module features/releases/hooks/useReleaseAgentAutoOpen
 * @description Auto-opens the assistant panel onto a "watch this agent"
 * thread the first time a release run's pane is viewed while its agent is
 * live, once per run per browser session (`sessionStorage`, not
 * `localStorage` — this shouldn't keep suppressing the auto-open days later
 * in the same browser). See `releaseAgentAutoOpen.ts` for the pure
 * decision/kickoff logic.
 */

import React from 'react';
import { useAssistant } from '../../assistant/AssistantContext';
import { useReleaseRun, useReleaseAgent } from '../useReleases';
import { buildReleaseAgentKickoff, markAutoOpened, shouldAutoOpenForRun } from '../releaseAgentAutoOpen';

function browserStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function useReleaseAgentAutoOpen({ runId }: { runId: string | null }) {
  const { startThreadWithKickoff } = useAssistant();
  const { data: runData } = useReleaseRun(runId ?? undefined);
  const durableAgentId = runData?.run?.durableAgentId ?? null;
  const { data: agentData } = useReleaseAgent(runId ?? undefined, !!durableAgentId);
  const agentStatus = agentData?.agent?.status;

  React.useEffect(() => {
    if (!runId || !agentStatus) return;
    const storage = browserStorage();
    if (!shouldAutoOpenForRun(runId, agentStatus, storage)) return;
    markAutoOpened(runId, storage);
    startThreadWithKickoff(buildReleaseAgentKickoff(runId));
  }, [runId, agentStatus, startThreadWithKickoff]);
}
